<template>
  <div class="product-price-details">
    <table class="product-price-details__prices">
      <tr
          v-for="(row) in rowsPrices"
          :key="row.label"
      >
        <td
            class="fs-regular"
            :colspan="row.value ? 1 : 2"
        >
          <span v-html="row.label"></span><sup class="d-none d-print-inline">{{ row.sub }}</sup>&nbsp;
          <IoTooltip v-if="row.info" class="color-primary d-print-none">
            <template v-slot:title>
              {{ row.info }}
            </template>
            <FontAwesomeIcon :icon="faInfoCircle" />
          </IoTooltip>
        </td>
        <td
            v-if="row.value"
            class="fs-regular"
            v-html="row.value"
        ></td>
      </tr>
    </table>
    <div
        v-if="rowsDownloads.length"
        class="product-price-details__downloads"
    >
      <a
          v-for="row in rowsDownloads"
          :key="`download--${row.key}`"
          :href="row.href"
          target="_blank"
      >
        <span>
            <FontAwesomeIcon :icon="faDownload" />
        </span>
        <span>
          {{ row.label }}
        </span>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import {
  computed,
} from 'vue';
import { formatNumber } from '@/helpers';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import Product from '@/classes/Product';

library.add(faInfoCircle, faDownload);

const props = defineProps<{
  product: Product
  withAddons?: boolean
}>();

const getAddonMonthlyBasePrice = (item: TelevisionPackageInterface) => item?.price?.recurring?.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0;
const getAddonMonthlyBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getAddonMonthlyBasePrice(item), 2);
const getAddonOneTimeBasePrice = (item: TelevisionPackageInterface) => item?.price?.oneTime?.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0;
const getAddonOneTimeBasePriceFormatted = (item: TelevisionPackageInterface) => formatNumber(getAddonOneTimeBasePrice(item), 2);

const addonsWithPrice = computed(() => props.product.addons.filter((addon) => (getAddonMonthlyBasePrice(addon.item) + getAddonOneTimeBasePrice(addon.item)) > 0));

const getAddonLabel = (addon) => {
  switch (addon.category) {
    case 'router':
      return `Router Upgrade: ${addon.item.name}`;
    case 'television-package':
      return `TV-${addon.item.name}`;
    case 'television-option':
      return `TV-Zusatzoption: ${addon.item.name}`;
    case 'television-addon':
      return `TV-Sprachpaket: ${addon.item.name}`;
    default:
      return addon.item.name;
  }
};

const rowsPrices = computed(() => {
  const rows: Record<any, unknown>[] = [];

  const savings = props.product.getPriceSavings();

  savings.forEach((saving, inx) => {
    let savingPrice = props.product.getRecurringPriceValue('base_price') - saving.value;
    if (savingPrice < 0) savingPrice = 0;

    rows.push({
      label: inx === 0 ? 'Preis pro Monat' : `Ab dem ${saving.fromMonth}. Monat`,
      value: `${formatNumber(savingPrice, 2)}&thinsp;€&thinsp;/&thinsp;Monat`,
    });
  });

  if (savings.length) {
    rows.push({
      label: `Ab dem ${savings[savings.length - 1].toMonth + 1}. Monat`,
      value: `${props.product.getRecurringPriceValueFormatted('base_price')}&thinsp;€&thinsp;/&thinsp;Monat`,
    });
  } else {
    rows.push({
      label: 'Preis pro Monat',
      value: `${props.product.getRecurringPriceValueFormatted('base_price')}&thinsp;€&thinsp;/&thinsp;Monat`,
    });
  }

  if (props.withAddons && addonsWithPrice.value.length) {
    addonsWithPrice.value.forEach((addon) => {
      if (addon.category !== 'service') {
        rows.push({
          label: getAddonLabel(addon),
          value: `${getAddonMonthlyBasePriceFormatted(addon.item)}&thinsp;€/Monat`,
        });
      }
    });
  }

  if (props.product.getOneTimePriceValueFormatted('setup_fee')) {
    rows.push({
      label: 'Bereitstellungsgebühr',
      value: `${props.product.getOneTimePriceValueFormatted('setup_fee')}&thinsp;€`,
    });
  } else {
    rows.push({
      label: 'Keine Bereitstellungsgebühr',
    });
  }

  if (props.withAddons && addonsWithPrice.value.length) {
    addonsWithPrice.value.forEach((addon) => {
      if (addon.category === 'service') {
        rows.push({
          label: getAddonLabel(addon),
          value: `${getAddonOneTimeBasePriceFormatted(addon.item)}&thinsp;€`,
        });
      }
    });
  }

  rows.push({
    label: 'Vertragslaufzeit',
    value: `${props.product.contractTermLabel}`,
  });

  rows.push({
    label: 'Vertragsverlängerung',
    value: 'Um jeweils 1 Monat',
  });

  return rows;
});

const rowsDownloads = computed(() => {
  const rows: Record<'label' | 'href' | 'key', string>[] = [];

  const infoDocument = props.product.getDocumentByKey('info');
  if (infoDocument) {
    rows.push({
      key: infoDocument.key,
      label: infoDocument.name || '',
      href: infoDocument.url || '#',
    });
  }

  const agbDocument = props.product.getDocumentByKey('agb');
  if (agbDocument) {
    rows.push({
      key: agbDocument.key,
      label: agbDocument.name || '',
      href: agbDocument.url || '#',
    });
  }

  const avbDocument = props.product.getDocumentByKey('avb');
  if (avbDocument) {
    rows.push({
      key: avbDocument.key,
      label: avbDocument.name || '',
      href: avbDocument.url || '#',
    });
  }

  return rows;
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.product-price-details {
  .product-price-details__prices {
    width: 100%;

    td {
      @include font-weight-fix;
      font-size: .75rem;
      line-height: 2;
      text-align: left;
      color: #293033;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: .875rem;
      }

      + td {
        white-space: nowrap;
        text-align: right;
        vertical-align: top;
      }

      &.spacer {
        &:before {
          content: ' ';
          display: inline-block;
        }
      }
    }

    a {
      text-decoration: none;
      color: inherit;

      i, svg {
        margin-right: .5em;
      }

      &:hover {
        color: $primary;
      }
    }
  }

  .product-price-details__downloads {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 10px;

    a {
      flex: 0 0 auto;
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
      align-items: center;
      font-size: .75rem;
      text-decoration: none;
      color: $primary;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: .875rem;
      }
    }
  }
}
</style>
